interface DemoFileReviewStageProps {
  onContinue: () => void;
}

// Restore the original File review mockup independently of project data.
const SAMPLE_TABLE_USAGE = [
  { category: "Trial balances", detected: 78, used: 75 },
  { category: "Management accounts", detected: 1305, used: 1148 },
  { category: "Sales and debtors", detected: 11792, used: 9552 },
  { category: "Purchases and creditors", detected: 10516, used: 8202 },
  { category: "Payroll", detected: 1630, used: 1369 },
  { category: "Bank and treasury", detected: 2321, used: 1601 },
  { category: "Inventory", detected: 1163, used: 709 },
  { category: "Contracts and legal", detected: 137, used: 10 },
];

const TOTAL_DETECTED = SAMPLE_TABLE_USAGE.reduce((sum, row) => sum + row.detected, 0);
const TOTAL_USED = SAMPLE_TABLE_USAGE.reduce((sum, row) => sum + row.used, 0);
const formatCount = (count: number) => count.toLocaleString("en-GB");

export function DemoFileReviewStage({
  onContinue,
}: DemoFileReviewStageProps) {
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="builder-stage-frame pb-10 pt-6">
        <div className="mb-6 flex items-center justify-between gap-3">
          <h1 className="text-xl font-semibold">File review</h1>
          <button
            type="button"
            onClick={onContinue}
            className="rounded-lg bg-foreground px-4 py-2 text-[12.5px] font-medium text-background hover:opacity-90"
          >
            Continue
          </button>
        </div>

        <section aria-label="Tables detected vs used" className="overflow-hidden rounded-xl border border-border/60 bg-card">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b border-border/60 px-4 py-2.5">
            <h2 className="text-[12.5px] font-semibold">Tables detected vs used</h2>
            <span className="text-[10.5px] text-muted-foreground">by file category</span>
            <span className="ml-auto text-[10.5px] text-muted-foreground">
              <span className="font-semibold text-foreground">{formatCount(TOTAL_USED)}</span>
              {" of "}{formatCount(TOTAL_DETECTED)} in use
            </span>
          </div>
          <div className="px-4 py-2">
            <table className="w-full text-[11px]">
              <thead className="sr-only">
                <tr>
                  <th scope="col">File category</th>
                  <th scope="col" className="hidden sm:table-cell">Usage</th>
                  <th scope="col">Tables used / detected</th>
                  <th scope="col">Percentage used</th>
                </tr>
              </thead>
              <tbody>
                {SAMPLE_TABLE_USAGE.map((row) => {
                  const percentage = Math.round(row.used / row.detected * 100);
                  return (
                    <tr key={row.category}>
                      <th scope="row" className="py-1 pr-3 text-left font-normal text-foreground/80 sm:w-[160px]">
                        {row.category}
                      </th>
                      <td className="hidden px-3 sm:table-cell">
                        <div aria-hidden="true" className="h-[7px] overflow-hidden rounded-sm bg-muted">
                          <div className="h-full bg-foreground" style={{ width: `${percentage}%` }} />
                        </div>
                      </td>
                      <td className="whitespace-nowrap text-right text-[10.5px] tabular-nums sm:w-24">
                        <span className="font-semibold">{formatCount(row.used)}</span>
                        <span className="text-muted-foreground/70"> / {formatCount(row.detected)}</span>
                      </td>
                      <td className="w-11 pl-2 text-right text-[10.5px] tabular-nums text-muted-foreground/70">
                        {percentage}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
